"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatFirebaseDate = exports.formatWatchedDate = exports.inferWatchedDatePrecision = exports.parseFirebaseDate = void 0;
const firestore_1 = require("firebase/firestore");
/**
 * Robustly parses a Firebase/Firestore date/timestamp into a JS Date object.
 * Handles:
 * - JS Date objects
 * - Firestore Timestamp objects
 * - Plain objects with seconds/_seconds (native adapter)
 * - Stringified timestamps: "Timestamp(seconds=X, nanoseconds=Y)"
 * - ISO string dates
 * - Numeric timestamps (milliseconds)
 *
 * @param value The value to parse
 * @returns A Date object or null if invalid
 */
const parseFirebaseDate = (value) => {
    var _a;
    if (!value)
        return null;
    // 1. Already a Date object
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }
    // 2. Firestore Timestamp object
    if (value instanceof firestore_1.Timestamp) {
        const date = value.toDate();
        return isNaN(date.getTime()) ? null : date;
    }
    // 3. Object with .toDate() method
    if (typeof value === 'object' && typeof value.toDate === 'function') {
        const date = value.toDate();
        return isNaN(date.getTime()) ? null : date;
    }
    // 4. Plain object with seconds or _seconds
    if (typeof value === 'object') {
        const seconds = (_a = value.seconds) !== null && _a !== void 0 ? _a : value._seconds;
        if (typeof seconds === 'number') {
            return new Date(seconds * 1000);
        }
        // Also check for numeric strings in seconds
        if (typeof seconds === 'string' && /^\d+$/.test(seconds)) {
            return new Date(parseInt(seconds, 10) * 1000);
        }
    }
    // 5. String parsing
    if (typeof value === 'string') {
        // Handle native Timestamp string format: "Timestamp(seconds=X, nanoseconds=Y)"
        const match = value.match(/Timestamp\(seconds=(\d+),\s*nanoseconds=\d+\)/);
        if (match) {
            return new Date(parseInt(match[1], 10) * 1000);
        }
        // Handle Firebase Auth metadata.creationTime format (RFC 3339)
        // Examples: "Wed, 28 Mar 2024 10:00:00 GMT", "2024-03-28T10:00:00.000Z"
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) {
            return parsed;
        }
        // Try parsing numeric strings (Unix timestamps in seconds or milliseconds)
        if (/^\d+$/.test(value)) {
            const numValue = parseInt(value, 10);
            // If it looks like seconds (before year 2100 in seconds), convert to ms
            if (numValue < 10000000000) {
                return new Date(numValue * 1000);
            }
            return new Date(numValue);
        }
    }
    // 6. Numeric timestamp (ms)
    if (typeof value === 'number' && !isNaN(value)) {
        return new Date(value);
    }
    return null;
};
exports.parseFirebaseDate = parseFirebaseDate;
/**
 * Infers display precision from a stored date when no explicit precision field is available.
 * Jan 1 = year-only entry (old imprecise records), other 1st = month entry, other day = full date.
 */
const inferWatchedDatePrecision = (d) => {
    if (d.getDate() !== 1)
        return 'day';
    if (d.getMonth() === 0)
        return 'year';
    return 'month';
};
exports.inferWatchedDatePrecision = inferWatchedDatePrecision;
/**
 * Formats a watched date in compact form respecting the stored precision level.
 * - 'year'  → "2026"
 * - 'month' → "Mar'26"
 * - 'day'   → "24 Mar'26"
 * Falls back to inferring precision from the date shape for backward compat.
 */
const formatWatchedDate = (value, precision, fallback = 'recently') => {
    const d = (0, exports.parseFirebaseDate)(value);
    if (!d)
        return fallback;
    const effective = precision !== null && precision !== void 0 ? precision : (0, exports.inferWatchedDatePrecision)(d);
    try {
        const yearShort = `'${String(d.getFullYear()).slice(2)}`;
        if (effective === 'year')
            return String(d.getFullYear());
        if (effective === 'month') {
            const mon = d.toLocaleDateString('en-GB', { month: 'short' });
            return `${mon}${yearShort}`;
        }
        const day = d.getDate();
        const mon = d.toLocaleDateString('en-GB', { month: 'short' });
        return `${day} ${mon}${yearShort}`;
    }
    catch (_a) {
        return fallback;
    }
};
exports.formatWatchedDate = formatWatchedDate;
/**
 * Formats a Firebase date into a consistent localized string.
 * Default format: "27 Mar 2026" (en-GB style)
 *
 * @param value The value to parse and format
 * @param fallback The fallback string if the date is invalid (default: 'recently')
 * @returns Formatted date string
 */
const formatFirebaseDate = (value, fallback = 'recently', options = {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
}) => {
    const date = (0, exports.parseFirebaseDate)(value);
    if (!date)
        return fallback;
    try {
        // Use en-GB for consistent day-month-year order across platforms
        return date.toLocaleDateString('en-GB', options);
    }
    catch (error) {
        console.error('Error formatting date:', error);
        return fallback;
    }
};
exports.formatFirebaseDate = formatFirebaseDate;
